import type { JSX } from 'react';
import { useIntl } from 'react-intl';

import * as styles from './DataTableDownloadData.sty.js';
import { useId } from '../../../../../core/hooks/useId.js';
import { Menu } from '../../../../../navigation/menu/Menu.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import { dataTableMessages } from '../../../messages.js';

/**
 * A component providing the menu with the functionalities to download data.
 * @internal
 */
export const DataTableDownloadData = ({
  excludeColumns,
  fileName,
  hasIntents,
  isDownloading,
  onDownloadStart,
  onDownloadEnd,
}: {
  excludeColumns?: string[];
  fileName?: string | ((subset: 'all' | 'page' | 'selected') => string);
  hasIntents: boolean;
  isDownloading: boolean;
  onDownloadStart: () => void;
  onDownloadEnd: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const table = useDataTable();
  const selectionCount = table.getSelectionCount();
  const menuGroupLabel = intl.formatMessage(dataTableMessages.downloadTable);

  // Hide download all option for server side pagination.
  const isServerSidePaginated =
    table.options.enablePagination === true &&
    table.options.manualPagination === true;

  // ids used for accessible names for download options, ex: "Download CSV All, 1,234 rows".
  // Skipped when intents are configured: the items are then in a Menu.SubContent,
  // which Radix already labels via the Menu.SubTrigger.
  const labelId = useId();
  const allItemId = useId();
  const pageItemId = useId();
  const selectedItemId = useId();

  const downloadMenuItems = (
    <>
      {!isServerSidePaginated && (
        <Menu.Item
          id={allItemId}
          aria-labelledby={hasIntents ? undefined : `${labelId} ${allItemId}`}
          onSelect={() => {
            if (isDownloading) {
              return;
            }
            onDownloadStart();
            table.downloadAllData(
              excludeColumns,
              undefined,
              fileName,
              onDownloadEnd,
            );
          }}
          className={styles.downloadMenuItem}
          data-dt-children-name="ToolbarDownloadAll"
        >
          {intl.formatMessage(dataTableMessages.downloadAll)}
          <Menu.Suffix className={styles.downloadMenuSuffix}>
            {intl.formatMessage(dataTableMessages.downloadRowsCount, {
              noOfRows: table.getPrePaginatedRowModel().flatRows.length,
            })}
          </Menu.Suffix>
        </Menu.Item>
      )}
      {table.options.enablePagination && (
        <Menu.Item
          id={pageItemId}
          aria-labelledby={hasIntents ? undefined : `${labelId} ${pageItemId}`}
          onSelect={() => {
            if (isDownloading) {
              return;
            }
            onDownloadStart();
            table.downloadPageData(
              excludeColumns,
              undefined,
              fileName,
              onDownloadEnd,
            );
          }}
          className={styles.downloadMenuItem}
          data-dt-children-name="ToolbarDownloadPage"
        >
          {intl.formatMessage(dataTableMessages.downloadPage)}
          <Menu.Suffix className={styles.downloadMenuSuffix}>
            {intl.formatMessage(dataTableMessages.downloadRowsCount, {
              noOfRows: table.getPaginatedRowModel().flatRows.length,
            })}
          </Menu.Suffix>
        </Menu.Item>
      )}
      {table.options.enableRowSelection && (
        <Menu.Item
          id={selectedItemId}
          aria-labelledby={
            hasIntents ? undefined : `${labelId} ${selectedItemId}`
          }
          disabled={selectionCount === 0}
          onSelect={() => {
            if (isDownloading) {
              return;
            }
            onDownloadStart();
            table.downloadSelectedData(
              excludeColumns,
              undefined,
              fileName,
              onDownloadEnd,
            );
          }}
          className={styles.downloadMenuItem}
          data-dt-children-name="ToolbarDownloadSelected"
        >
          {intl.formatMessage(dataTableMessages.downloadSelected)}
          <Menu.Suffix className={styles.downloadMenuSuffix}>
            {intl.formatMessage(dataTableMessages.downloadRowsCount, {
              noOfRows: selectionCount,
            })}
          </Menu.Suffix>
        </Menu.Item>
      )}
    </>
  );

  return hasIntents ? (
    <Menu.Sub defaultOpen={false}>
      <Menu.SubTrigger>{menuGroupLabel}</Menu.SubTrigger>
      <Menu.SubContent>{downloadMenuItems}</Menu.SubContent>
    </Menu.Sub>
  ) : (
    <>
      <Menu.Label id={labelId}>{menuGroupLabel}</Menu.Label>
      {downloadMenuItems}
    </>
  );
};
